'use client'

import React, { FC, ReactElement } from 'react'
import Wallet from '@/utils/wallet'
import { useSignIn, useLogout } from '@/utils/hooks/useLoggedIn'
import event from '@/utils/event'

import { CommandItem } from '@/components/ui/command'
import { useRouter } from 'next/navigation'
import type { WalletProvider } from '@/utils/types/wallet'
import {
	classifyWalletError,
	connectWalletWithTelemetry,
	walletErrorMessage
} from '@/utils/wallet/telemetry'
import { TAP_WALLET_STORE_URL } from '@/utils/wallet/tap'

const ConnectWallet: FC<{ onClose: () => void }> = (props): ReactElement => {
	const { onClose } = props
	const providers = React.useMemo(() => Wallet.providers(), [])
	const signIn = useSignIn()
	const logout = useLogout()
	const router = useRouter()
	const [connectingProvider, setConnectingProvider] = React.useState<string | null>(null)
	const [status, setStatus] = React.useState<string | null>(null)
	const [connectionError, setConnectionError] = React.useState<{
		message: string
		showTapInstall: boolean
	} | null>(null)
	const errorRef = React.useRef<HTMLDivElement>(null)
	const statusRef = React.useRef<HTMLParagraphElement>(null)
	const connectionAttemptRef = React.useRef(false)

	React.useEffect(() => {
		if (!connectionError) return
		errorRef.current?.scrollIntoView({ block: 'nearest' })
	}, [connectionError])

	React.useEffect(() => {
		if (!status) return
		statusRef.current?.scrollIntoView({ block: 'nearest' })
	}, [status])

	const renderProvider = React.useCallback(
		(provider: WalletProvider) => {
			const isConnecting = connectingProvider === provider.name
			return (
				<CommandItem
					key={`connect-${provider.name}`}
					disabled={connectingProvider !== null}
					aria-busy={isConnecting}
					onSelect={async () => {
						if (connectionAttemptRef.current || connectingProvider) return
						connectionAttemptRef.current = true

						try {
							if (Wallet.type() === 'ordinalswallet' && Wallet.isSet()) {
								return event.emit('open-signout-modal')
							}

							if (provider.name === 'Ordinals Wallet') {
								router.push('/create-wallet')
								onClose()
								return
							}

							setConnectingProvider(provider.name)
							setConnectionError(null)
							setStatus(`Approve the connection in ${provider.name}.`)

							// Tap commits its session atomically after validation. Keep the existing
							// wallet intact if Tap is missing, locked, or the user cancels.
							if (provider.name !== 'Tap Wallet') {
								logout()
							}

							const connected = await connectWalletWithTelemetry(
								provider,
								'command_palette',
								() => setStatus(`Still waiting for ${provider.name}. Open or unlock the extension.`)
							)

							if (connected) {
								signIn()
								onClose()
							}
						} catch (error) {
							const category = classifyWalletError(error)
							const message = walletErrorMessage(error, provider.name)
							setStatus(null)
							setConnectionError({
								message,
								showTapInstall: provider.name === 'Tap Wallet' && category === 'provider_missing'
							})
							if (category !== 'user_rejected') {
								event.emit('snackbar', { variant: 'error', title: message })
							}
						} finally {
							connectionAttemptRef.current = false
							setConnectingProvider(null)
						}
					}}
				>
					<img className="size-8 mr-4" src={provider.connectIcon} alt="" />
					<span>{provider.name}</span>
					{isConnecting && (
						<span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
							<span className="size-4 rounded-full border-2 border-current/25 border-t-current animate-spin" />
							Connecting
						</span>
					)}
				</CommandItem>
			)
		},
		[connectingProvider, signIn, onClose, logout, router]
	)

	return (
		<>
			{providers.map(renderProvider)}
			{status && (
				<p
					ref={statusRef}
					className="mx-2 my-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
					role="status"
				>
					{status}
				</p>
			)}
			{connectionError && (
				<div
					ref={errorRef}
					className="mx-2 my-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs"
					role="alert"
				>
					<p>{connectionError.message}</p>
					{connectionError.showTapInstall && (
						<a
							className="mt-2 inline-flex font-medium text-primary underline underline-offset-4"
							href={TAP_WALLET_STORE_URL}
							target="_blank"
							rel="noreferrer noopener"
						>
							Open the official Tap Wallet extension
						</a>
					)}
				</div>
			)}
		</>
	)
}

export default ConnectWallet
